import { SiteLayout, PageHero, LightSection, CtaStrip } from "@/components/site/SiteLayout";

const DELIVERABLES = [
  ["A governed readiness verdict", "One transparent global score and a market-by-market position — the number your leadership team can steer by, with every assumption visible."],
  ["A prioritized evidence roadmap", "Not a list of everything that could be done — a sequence of what should be done, ranked by how much readiness each investment buys."],
  ["A defensible submission sequence", "Which markets, in which order, and why — accounting for precedents, dependencies and reference-pricing spillovers."],
  ["An evidence reuse map", "Exactly which assets travel to which markets, what needs adaptation, and where local generation is unavoidable."],
  ["A value dossier enhancement plan", "Where your GVD holds, where it doesn't, and the ordered plan to make one document carry many markets."],
  ["A board-ready decision package", "Presented and defended in person — findings, options, recommendation, business case."],
];

export default function Outcomes() {
  return (
    <SiteLayout>
      <PageHero overline="Outcomes" title="What you walk away with."
        lede="Every engagement ends in decisions, not documents. These are the six deliverables leadership teams use long after the readout." />
      <LightSection>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {DELIVERABLES.map(([title, text]) => (
            <div key={title} className="bg-white border border-slate-200 p-6">
              <p className="font-heading font-bold text-slate-900 text-sm">{title}</p>
              <p className="text-sm text-slate-500 leading-relaxed mt-2">{text}</p>
            </div>
          ))}
        </div>
      </LightSection>
      <LightSection alt overline="A representative engagement" title="From debated opinions to a governed plan — in six weeks.">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-4 bg-white border border-slate-200 p-6">
            <p className="overline-label text-slate-400">The situation</p>
            <p className="text-sm text-slate-500 leading-relaxed mt-3">A MedTech innovator with strong pivotal data and regulatory approvals in early markets, planning expansion into a dozen reimbursement-driven markets. Every function had a different launch order; nobody could say which markets were actually ready.</p>
          </div>
          <div className="lg:col-span-4 bg-white border border-slate-200 p-6">
            <p className="overline-label text-slate-400">What we found</p>
            <ul className="mt-3 space-y-2.5 text-sm text-slate-500">
              <li className="flex gap-2.5"><span className="text-red-300">✕</span>Two markets assumed "ready" had mandatory local-evidence expectations nobody had planned for</li>
              <li className="flex gap-2.5"><span className="text-red-300">✕</span>A single economic analysis, adapted, could serve five markets — it was budgeted five separate times</li>
              <li className="flex gap-2.5"><span className="text-red-300">✕</span>The intended first market would have set a pricing anchor that damaged three larger ones</li>
            </ul>
          </div>
          <div className="lg:col-span-4 bg-slate-900 p-6">
            <p className="overline-label text-emerald-400">The outcome</p>
            <div className="flex items-end gap-3 mt-3">
              <p className="font-heading font-extrabold text-slate-500 text-3xl line-through">52</p>
              <p className="font-heading font-extrabold text-white text-5xl">78</p>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2">Global readiness</p>
            </div>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-300">
              <li className="flex gap-2.5"><span className="text-emerald-400">✓</span>Submission sequence re-ordered before the anchor was set</li>
              <li className="flex gap-2.5"><span className="text-emerald-400">✓</span>Evidence budget consolidated around reusable assets</li>
              <li className="flex gap-2.5"><span className="text-emerald-400">✓</span>Board approved a sequenced, prioritized roadmap in one sitting</li>
            </ul>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-5">Illustrative engagement — figures anonymized and representative of a typical Global Readiness Assessment.</p>
      </LightSection>
      <CtaStrip text="One engagement. One verdict. One roadmap." />
    </SiteLayout>
  );
}
